import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Brain, Compass, Eye, Sparkles, Shield, Wallet, Heart, Briefcase,
  ArrowRight, Share2, Zap, Target,
} from "lucide-react";
import { track } from "@/lib/api";
import { getMe, loginWithGoogle } from "@/lib/auth";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.2, 0.7, 0.2, 1] } }),
};

const BENEFITS = [
  { icon: Sparkles, title: "Karakter unik", desc: "Bukan hasil generik. Karakter yang kerasa 'ini gue banget'." },
  { icon: Shield, title: "Kekuatan lo", desc: "Hal yang selama ini lo remehin, padahal itu senjata lo." },
  { icon: Eye, title: "Blind spot lo", desc: "Pola yang sering lo ulang tanpa sadar." },
  { icon: Briefcase, title: "Pola kerja", desc: "Kenapa lo capek, kenapa lo betah, kenapa lo mentok." },
  { icon: Heart, title: "Pola hubungan", desc: "Cara lo ngasih, nerima, dan naruh batasan." },
  { icon: Wallet, title: "Pola uang", desc: "Hubungan lo sama duit, jujur tanpa ngeceramahin." },
];

const EXAMPLES = [
  { icon: "🪞", name: "GAYOT KACA", tagline: "Kuat di luar. Banyak retak di dalam.", grad: "from-blue-500/25" },
  { icon: "🔋", name: "POWERBANK MANUSIA", tagline: "Semua nge-charge dari lo. Siapa yang ngisi lo?", grad: "from-emerald-500/25" },
  { icon: "🧭", name: "KOMPAS RUSAK", tagline: "Banyak tujuan, terlalu banyak arah orang.", grad: "from-amber-400/25" },
];

export default function Landing() {
  const navigate = useNavigate();
  useEffect(() => { track("landing_page_view"); }, []);

  const enter = async () => {
    track("checkout_click");
    try { await getMe(); navigate("/pilih"); }
    catch { loginWithGoogle(); }
  };

  return (
    <div className="relative min-h-screen w-full">
      {/* Nav */}
      <header className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pt-6 flex items-center justify-between">
        <div className="flex items-center" data-testid="nav-logo">
          <img src="/logo-nav.webp" alt="Ruang Kendali" className="h-12 sm:h-14 w-auto rounded-xl" />
        </div>
        <button
          onClick={enter}
          data-testid="nav-cta-button"
          className="hidden sm:inline-flex items-center gap-2 text-sm text-amber-200/80 hover:text-amber-100 transition-colors"
        >
          Mulai <ArrowRight size={16} />
        </button>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 pt-16 sm:pt-24 pb-10 text-center">
        <motion.p variants={fadeUp} initial="hidden" animate="show" custom={0}
          className="text-xs font-semibold tracking-[0.3em] uppercase text-amber-400/80 mb-6">
          Pikir. Hitung. Putuskan.
        </motion.p>
        <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={1}
          className="font-display text-5xl sm:text-7xl lg:text-8xl font-semibold leading-[0.95] tracking-tight">
          RUANG <span className="gold-text">KENDALI</span>
        </motion.h1>
        <motion.p variants={fadeUp} initial="hidden" animate="show" custom={2}
          className="mt-7 text-base sm:text-xl text-gray-300 font-light leading-relaxed max-w-xl mx-auto">
          Kadang masalah hidup bukan karena lo nggak punya pilihan.
          <br className="hidden sm:block" />
          Lo cuma belum ngeliat masalahnya dari sudut yang tepat.
        </motion.p>

        <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="mt-10">
          <button
            onClick={enter}
            data-testid="hero-cta-button"
            className="gold-btn rounded-full px-8 py-4 text-base sm:text-lg inline-flex items-center gap-3"
          >
            CEK KARAKTER GUE
            <ArrowRight size={20} />
          </button>
          <p className="mt-4 text-xs text-gray-500">Refleksi berdasarkan jawaban lo. Bukan diagnosis. Bukan psikotes.</p>
        </motion.div>

        {/* floating example */}
        <motion.div variants={fadeUp} initial="hidden" animate="show" custom={4}
          className="mt-16 relative mx-auto max-w-xs">
          <div className="card-surface rounded-3xl p-6 animate-float">
            <div className="text-6xl mb-3">🪞</div>
            <div className="font-display text-2xl gold-text">GAYOT KACA</div>
            <div className="mt-2 font-mono text-amber-300 text-sm">78% KESESUAIAN</div>
            <p className="mt-3 text-sm text-gray-400 italic">"Kuat di luar. Banyak retak di dalam."</p>
          </div>
        </motion.div>
      </section>

      {/* What you get */}
      <section className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 py-20">
        <h2 className="font-display text-3xl sm:text-5xl font-medium text-center mb-3">
          Yang bakal lo <span className="gold-text">dapetin</span>
        </h2>
        <p className="text-center text-gray-400 max-w-xl mx-auto mb-12 text-sm sm:text-base">
          Bukan nasihat kosong. Ini cerminan pola lo, dari cara lo jawab pertanyaan.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {BENEFITS.map((b, i) => (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
              className="card-surface rounded-2xl p-6 hover:border-amber-400/40 transition-colors duration-300 group"
            >
              <div className="w-11 h-11 rounded-xl bg-amber-400/10 border border-amber-400/20 flex items-center justify-center mb-4 group-hover:bg-amber-400/20 transition-colors">
                <b.icon size={20} className="text-amber-300" />
              </div>
              <h3 className="text-lg font-semibold text-amber-100/90">{b.title}</h3>
              <p className="mt-2 text-sm text-gray-400 leading-relaxed">{b.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Example characters */}
      <section className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 py-16">
        <p className="text-center text-xs font-semibold tracking-[0.25em] uppercase text-amber-400/70 mb-3">Contoh hasil</p>
        <h2 className="font-display text-3xl sm:text-5xl font-medium text-center mb-12">
          Lo bakal jadi karakter yang <span className="gold-text">mana?</span>
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {EXAMPLES.map((c, i) => (
            <motion.div key={c.name}
              initial={{ opacity: 0, scale: 0.92 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className={`relative rounded-3xl overflow-hidden border border-amber-400/15 bg-gradient-to-b ${c.grad} to-slate-900 p-7 text-center min-h-[280px] flex flex-col items-center justify-center`}
            >
              <div className="text-7xl mb-4">{c.icon}</div>
              <div className="font-display text-2xl gold-text">{c.name}</div>
              <p className="mt-3 text-sm text-gray-300 italic">"{c.tagline}"</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Viral / share note */}
      <section className="relative z-10 max-w-3xl mx-auto px-5 sm:px-8 py-16">
        <div className="glass rounded-3xl p-8 sm:p-12 text-center">
          <div className="flex justify-center gap-4 mb-6 text-amber-300">
            <Zap size={22} /><Share2 size={22} /><Target size={22} />
          </div>
          <h2 className="font-display text-2xl sm:text-4xl font-medium leading-tight">
            Dapet kartu hasil yang bisa lo <span className="gold-text">share</span> ke Story
          </h2>
          <p className="mt-4 text-gray-400 text-sm sm:text-base max-w-lg mx-auto">
            Visual karakter personal + kartu 9:16 siap buat Instagram, TikTok, dan WhatsApp.
            Bikin temen lo mikir: "gue penasaran karakter gue apa."
          </p>
          <button onClick={enter} data-testid="mid-cta-button"
            className="mt-8 gold-btn rounded-full px-8 py-4 inline-flex items-center gap-3">
            MASUK KE RUANG KENDALI <ArrowRight size={18} />
          </button>
        </div>
      </section>

      <footer className="relative z-10 border-t border-white/5 mt-10">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <div className="font-display text-lg tracking-[0.15em] font-semibold">RUANG <span className="gold-text">KENDALI</span></div>
            <p className="text-xs text-gray-500 mt-1">Pikir. Hitung. Putuskan.</p>
          </div>
          <div className="flex items-center gap-6 text-xs text-gray-500">
            <span>ruangkendali.app</span>
            <a href="/admin" data-testid="footer-admin-link" className="hover:text-amber-300 transition-colors">Admin</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
